import { useEffect, useState } from 'react';
import { precedentApi, type Precedent } from '../api';
import Select from '../components/common/Select';
import { humanise } from '../lib/labels';

const CATEGORIES = [
  'pleading', 'notice_of_motion', 'heads_of_argument', 'letter_of_demand',
  'covering_letter', 'practice_note', 'opinion', 'judgment', 'contract', 'other',
] as const;
type Category = typeof CATEGORIES[number];

const CATEGORY_OPTIONS = CATEGORIES.map((c) => ({ value: c, label: humanise(c) }));

export default function Precedents() {
  const [items, setItems] = useState<Precedent[]>([]);
  const [q, setQ] = useState('');
  const [category, setCategory] = useState<Category | ''>('');
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Precedent | 'new' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const r = await precedentApi.list({ q: q || undefined, category: category || undefined });
      setItems(r.precedents);
    } catch (e) {
      setError((e as Error).message);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const remove = async (p: Precedent) => {
    if (!confirm(`Delete precedent "${p.title}"? This cannot be undone.`)) return;
    try { await precedentApi.remove(p.ROWID); await load(); }
    catch (e) { setError((e as Error).message); }
  };

  return (
    <div className="stack">
      <header className="row">
        <div className="grow">
          <h1>Precedent library</h1>
          <p className="muted">Re-usable templates for letters, pleadings, opinions, and more. The AI assistant can search these to bootstrap a draft.</p>
        </div>
        <button className="primary" onClick={() => setEditing('new')}>+ New precedent</button>
      </header>

      <div className="row">
        <input
          className="grow"
          placeholder="Search title, summary, or tags…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') load(); }}
        />
        <Select
          value={category}
          onChange={(v) => setCategory(v as Category | '')}
          style={{ width: 200 }}
          options={[{ value: '', label: 'All categories' }, ...CATEGORY_OPTIONS]}
        />
        <button onClick={load}>Search</button>
      </div>

      {error && <div style={{ color: 'var(--color-danger)' }}>{error}</div>}

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {loading ? (
          <div className="muted" style={{ padding: 18 }}>Loading…</div>
        ) : items.length === 0 ? (
          <div className="muted" style={{ padding: 18 }}>
            No precedents yet. Click <b>+ New precedent</b> to add your first template.
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Title</th>
                <th>Category</th>
                <th>Summary</th>
                <th>Tags</th>
                <th>Updated</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <tr key={p.ROWID}>
                  <td style={{ fontWeight: 500 }}>
                    <a onClick={() => setEditing(p)} style={{ cursor: 'pointer' }}>{p.title}</a>
                  </td>
                  <td><span className="pill">{humanise(p.category)}</span></td>
                  <td className="muted" style={{ fontSize: 12, maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {p.summary || '—'}
                  </td>
                  <td className="muted" style={{ fontSize: 12 }}>{p.tags || '—'}</td>
                  <td className="muted" style={{ fontSize: 12 }}>{(p.MODIFIEDTIME || '').slice(0, 10)}</td>
                  <td style={{ textAlign: 'right' }}>
                    <button onClick={() => remove(p)} className="danger">Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {editing && (
        <Editor
          precedent={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

// ---------- Editor modal ----------

function Editor({
  precedent,
  onClose,
  onSaved,
}: {
  precedent: Precedent | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isNew = !precedent;
  const [form, setForm] = useState({
    title: precedent?.title || '',
    category: (precedent?.category as Category) || 'letter_of_demand',
    summary: precedent?.summary || '',
    body: precedent?.body || '',
    tags: precedent?.tags || '',
  });
  // Existing precedents need their full body fetched — list endpoint omits it.
  const [bodyLoading, setBodyLoading] = useState(!isNew && !precedent?.body);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isNew || precedent?.body) return;
    precedentApi.get(precedent!.ROWID)
      .then((r) => setForm((f) => ({ ...f, body: r.precedent.body || '' })))
      .catch(() => null)
      .finally(() => setBodyLoading(false));
  }, [isNew, precedent]);

  const save = async () => {
    if (!form.title.trim()) { setError('Title is required.'); return; }
    setSaving(true); setError(null);
    try {
      if (isNew) {
        await precedentApi.create({
          title: form.title.trim(),
          category: form.category,
          summary: form.summary || undefined,
          body: form.body || undefined,
          tags: form.tags || undefined,
        });
      } else {
        await precedentApi.update(precedent!.ROWID, {
          title: form.title.trim(),
          category: form.category,
          summary: form.summary,
          body: form.body,
          tags: form.tags,
        });
      }
      onSaved();
    } catch (e) {
      setError((e as Error).message);
    } finally { setSaving(false); }
  };

  return (
    <div style={backdrop} onClick={onClose}>
      <div className="card" style={modalCard} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ marginBottom: 14 }}>{isNew ? 'New precedent' : 'Edit precedent'}</h3>
        <div className="stack">
          <div className="row">
            <div className="grow">
              <label>Title</label>
              <input
                autoFocus
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="e.g. Letter of demand — unpaid fees"
              />
            </div>
            <div style={{ width: 240 }}>
              <label>Category</label>
              <Select
                value={form.category}
                onChange={(v) => setForm({ ...form, category: v as Category })}
                options={CATEGORY_OPTIONS}
              />
            </div>
          </div>

          <div>
            <label>Summary (one line — shown in search results)</label>
            <input
              value={form.summary}
              onChange={(e) => setForm({ ...form, summary: e.target.value })}
              placeholder="A short hint about when to use this template."
            />
          </div>

          <div>
            <label>Tags (comma-separated)</label>
            <input
              value={form.tags}
              onChange={(e) => setForm({ ...form, tags: e.target.value })}
              placeholder="commercial, urgent, draft"
            />
          </div>

          <div>
            <label>Body (Markdown supported)</label>
            {bodyLoading ? (
              <div className="muted" style={{ padding: 12 }}>Loading body…</div>
            ) : (
              <textarea
                value={form.body}
                onChange={(e) => setForm({ ...form, body: e.target.value })}
                rows={14}
                style={{ resize: 'vertical', width: '100%', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 12.5 }}
                placeholder={'Dear Sir or Madam,\n\nWe act for…'}
              />
            )}
          </div>

          {error && <div style={{ color: 'var(--color-danger)' }}>{error}</div>}

          <div className="row" style={{ justifyContent: 'flex-end', gap: 8 }}>
            <button onClick={onClose} disabled={saving}>Cancel</button>
            <button className="primary" onClick={save} disabled={saving || bodyLoading}>
              {saving ? 'Saving…' : isNew ? 'Create' : 'Save changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const backdrop: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100,
};
const modalCard: React.CSSProperties = { width: 760, maxWidth: '92vw', maxHeight: '88vh', overflowY: 'auto' };
